import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { db } from '@/firebase';
import { removeTeamId, setTeamId } from '@/utils/localStorage';
import { CURR_SESSION_STATES } from '@/constants/constants';
import {
	Announcement,
	ClubOffer,
	AppOffer,
	BalanceAccount,
	DeploymentImage,
	HardwareOffer,
	Session,
	SessionBackgroundTask,
	SetupSessionPrefill,
	SoftwareOffer,
	StableDiffusionModel,
	StableDiffusionOffer,
	StripeCustomer,
	Team,
	User,
	UserRunnitNodeDefInfo,
	UserWorkflowRunnitInfo,
	Workspace,
	Runnit,
	RunnitNode,
	RunnitNodeDef,
	RunnitNodeRun,
	RunnitNodeDefTag,
	RunnitKit,
	Model,
	CompanyUserRole,
	RUNNITS_ACCESS_LEVEL,
	RUNNIT_TYPE,
	RUNNIT_NODE_DEF_TOOL_TYPE,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RUNNIT_NODE_DEF_TAG_TYPE,
} from '@run-diffusion/shared';
import _isArray from 'lodash/isArray';
import { FREE_PLAN_LIMIT_CLUB_ADD_ON_NUM_RUNNITS_LIMIT } from '@/views/Runnits/constants';

export const get$bindFirestoreOptions: Function = (options: any = {}): any => ({ reset: false, wait: true, ...options });
export const buildNewVuefireBindingObj: Function = (val: any, otherProps: any): Object | null => {
	return val ? {
		...val,
		...otherProps,
		get id () { return val.id },
	} : null;
};
export const buildNewVuefireBindingArray: Function = (val: any[] | null): any[] => {
	if (!_isArray(val)) return [];
	return [...val];
};

export const VuexFirestoreBindingsMixin = Vue.extend({
	data () {
		return {
			vuefireClubOffers: [],
			vuefireAppOffers: [],
			vuefireHardwareOffers: [],
			vuefireSoftwareOffers: [],
			vuefireDeploymentImagesSystem: [],
			vuefireDeploymentImagesOwn: [],
			vuefireDeploymentImagesTeam: [],
			vuefireStableDiffusionOffers: [],
			vuefireWorkspaces: [],
			vuefireLimitNumRunnits: [],
			vuefireRunnitNodes: [],
			vuefireTrainerNodeDefs: [],
			vuefirePublicRunnitNodeDefs: [],
			vuefireCompanyRunnitNodeDefs: [],
			vuefirePublicRunnitNodeDefTags: [],
			vuefireRunnitKits: [],
			vuefireDraftRunnitNodeRuns: [],
			vuefireAnnouncements: [],
			vuefireUser: null,
			vuefireUserBalanceAccount: null,
			vuefireTeam: null,
			vuefireTeamBalanceAccount: null,
			vuefireStripeCustomer: null,
			vuefireStripeTeamsCustomer: null,
			vuefireCurrSessions: [],
			vuefireCurrSessionBackgroundTasks: [],
			vuefireAdminRegularSessions: [],
			vuefireStableDiffusionModels: [],
			vuefireSetupSessionPrefill: null,
			vuefireRecentUserRunnitNodeDefInfos: [],
			vuefireRecentUserWorkflowRunnitInfos: [],
			vuefireModels: [],
			vuefireCompanyUserRole: null,
			vuefireTeamUserRole: null,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'boundPropsClubOffers',
			'boundPropsAppOffers',
			'boundPropsHardwareOffers',
			'boundPropsSoftwareOffers',
			'boundPropsDeploymentImages',
			'boundPropsStableDiffusionOffers',
			'boundPropsWorkspaces',
			'boundPropsLimitNumRunnits',
			'boundPropsRunnitNodes',
			'boundPropsTrainerNodeDefs',
			'boundPropsPublicRunnitNodeDefs',
			'boundPropsCompanyRunnitNodeDefs',
			'boundPropsPublicRunnitNodeDefTags',
			'boundPropsRunnitKits',
			'boundPropsDraftRunnitNodeRun',
			'boundPropsAnnouncements',
			'boundPropsUser',
			'boundPropsUserBalanceAccount',
			'boundPropsTeam',
			'boundPropsTeamBalanceAccount',
			'boundPropsStripeCustomer',
			'boundPropsStripeTeamsCustomer',
			'boundPropsCurrSessions',
			'boundPropsCurrSessionBackgroundTasks',
			'boundPropsAdminRegularSessions',
			'boundPropsStableDiffusionModels',
			'boundPropsSetupSessionPrefill',
			'boundPropsRecentUserRunnitNodeDefInfos',
			'boundPropsRecentUserWorkflowRunnitInfos',
			'boundPropsModels',
			'boundPropsCompanyUserRole',
			'boundPropsTeamUserRole',
		]),
	},
	watch: {
		/*
		BoundProps
		 */
		boundPropsClubOffers: {
			immediate: true,
			async handler () {
				this.setLoadingClubOffers(true);
				const query = db.collection('clubOffers')
					.where('publishedAt', '!=', null);

				try {
					const newResult: ClubOffer[] = await this.$bind(
						'vuefireClubOffers',
						query,
						get$bindFirestoreOptions(),
					);
					if (!newResult) this.resultClubOffers(newResult);
				} catch (error) {
					console.error('Failed to load ClubOffers:', error);
					this.resultClubOffers([]); // Set to empty array in case of error
				} finally {
					this.setLoadingClubOffers(false);
				}
			},
		},
		boundPropsAppOffers: {
			immediate: true,
			async handler ({ user }) {
				if (user) {
					this.setLoadingAppOffers(true);
					let appOffersRef: any = db.collection('appOffers');
					if (!user.isAdmin && !user.isDev) {
						// index created: appOffers: publishedAt Ascending sortOrder Ascending __name__ Ascending
						appOffersRef = appOffersRef
							.where('publishedAt', '!=', null);
					}

					const newResult: AppOffer[] = await this.$bind(
						'vuefireAppOffers',
						appOffersRef,
						get$bindFirestoreOptions(),
					);
					this.setLoadingAppOffers(false);
					if (!newResult) this.resultAppOffers(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireAppOffers']) {
						this.$unbind('vuefireAppOffers');
					}
				}
			},
		},
		boundPropsHardwareOffers: {
			immediate: true,
			async handler ({ user }) {
				if (user) {
					this.setLoadingHardwareOffers(true);
					let hardwareOffersRef: any = db.collection('hardwareOffers');
					if (!user.isAdmin && !user.isDev) {
						// index created: hardwareOffers: publishedAt Ascending sortOrder Ascending __name__ Ascending
						hardwareOffersRef = hardwareOffersRef
							.where('publishedAt', '!=', null);
					}

					const newResult: HardwareOffer[] = await this.$bind(
						'vuefireHardwareOffers',
						hardwareOffersRef,
						get$bindFirestoreOptions(),
					);
					this.setLoadingHardwareOffers(false);
					if (!newResult) this.resultHardwareOffers(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireHardwareOffers']) {
						this.$unbind('vuefireHardwareOffers');
					}
				}
			},
		},
		boundPropsSoftwareOffers: {
			immediate: true,
			async handler ({ user }) {
				if (user) {
					this.setLoadingSoftwareOffers(true);
					let softwareOffersRef: any = db.collection('softwareOffers');
					if (!user.isAdmin && !user.isDev) {
						// index created: softwareOffers: publishedAt Ascending sortOrder Ascending __name__ Ascending
						softwareOffersRef = softwareOffersRef
							.where('publishedAt', '!=', null);
					}

					const newResult: SoftwareOffer[] = await this.$bind(
						'vuefireSoftwareOffers',
						softwareOffersRef,
						get$bindFirestoreOptions(),
					);
					this.setLoadingSoftwareOffers(false);
					if (!newResult) this.resultSoftwareOffers(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireSoftwareOffers']) {
						this.$unbind('vuefireSoftwareOffers');
					}
				}
			},
		},
		boundPropsDeploymentImages: {
			immediate: true,
			async handler ({ user }) {
				const team = this.team;
				const unbindIfBound = (key: string) => {
					if (this._firestoreUnbinds && this._firestoreUnbinds[key]) {
						this.$unbind(key);
					}
				};

				if (!user) {
					unbindIfBound('vuefireDeploymentImagesSystem');
					unbindIfBound('vuefireDeploymentImagesOwn');
					unbindIfBound('vuefireDeploymentImagesTeam');
					this.vuefireDeploymentImagesSystem = [];
					this.vuefireDeploymentImagesOwn = [];
					this.vuefireDeploymentImagesTeam = [];
					return;
				}

				this.setLoadingDeploymentImages(true);
				const applyIsPublishedFilter: boolean = !user.isAdmin && !user.isDev;
				const base: any = db.collection('deploymentImages');

				// Rule-aligned splits — see firestore.rules `/deploymentImages/{id}` read branches.
				// `isPublished == true` is an equality filter, so the compound queries below don't
				// require composite indexes (unlike the prior `publishedAt != null` inequality).
				let systemRef: any = base
					.where('user', '==', null)
					.where('team', '==', null);
				let ownRef: any = base.where('user', '==', user.id);
				if (applyIsPublishedFilter) {
					systemRef = systemRef.where('isPublished', '==', true);
					ownRef = ownRef.where('isPublished', '==', true);
				}

				const bindPromises: Promise<any>[] = [
					this.$bind('vuefireDeploymentImagesSystem', systemRef, get$bindFirestoreOptions()),
					this.$bind('vuefireDeploymentImagesOwn', ownRef, get$bindFirestoreOptions()),
				];

				if (team && team.id) {
					let teamRef: any = base.where('team', '==', team.id);
					if (applyIsPublishedFilter) {
						teamRef = teamRef.where('isPublished', '==', true);
					}
					bindPromises.push(this.$bind('vuefireDeploymentImagesTeam', teamRef, get$bindFirestoreOptions()));
				} else {
					unbindIfBound('vuefireDeploymentImagesTeam');
					this.vuefireDeploymentImagesTeam = [];
				}

				await Promise.all(bindPromises);
				this.setLoadingDeploymentImages(false);
			},
		},
		boundPropsStableDiffusionOffers: {
			immediate: true,
			async handler ({ user }) {
				if (user) {
					this.setLoadingStableDiffusionOffers(true);
					let stableDiffusionOffersRef: any = db.collection('stableDiffusionOffers');
					if (!user.isAdmin) {
						// index created: stableDiffusionOffers: publishedAt Ascending sortOrder Ascending __name__ Ascending
						stableDiffusionOffersRef = stableDiffusionOffersRef
							.where('publishedAt', '!=', null);
					}

					const newResult: StableDiffusionOffer[] = await this.$bind(
						'vuefireStableDiffusionOffers',
						stableDiffusionOffersRef,
						get$bindFirestoreOptions(),
					);
					this.setLoadingStableDiffusionOffers(false);
					if (!newResult) this.resultStableDiffusionOffers(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireStableDiffusionOffers']) {
						this.$unbind('vuefireStableDiffusionOffers');
					}
				}
			},
		},
		boundPropsWorkspaces: {
			immediate: true,
			async handler ({ teamId }) {
				if (teamId) {
					this.setLoadingWorkspaces(true);
					// TODO: Add pagination
					const newResult: Workspace[] = await this.$bind(
						'vuefireWorkspaces',
						// index created: workspaces: deletedAt Ascending teamId Ascending createdAt Ascending __name__ Ascending
						db.collection(`workspaces`)
							.where('teamId', '==', teamId)
							.where('deletedAt', '==', null)
							.orderBy('createdAt', 'asc')
							.limit(100),
						get$bindFirestoreOptions(),
					);
					this.setLoadingWorkspaces(false);
					if (!newResult) this.resultWorkspaces(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireWorkspaces']) {
						this.$unbind('vuefireWorkspaces');
					}
				}
			},
		},
		boundPropsLimitNumRunnits: {
			immediate: true,
			async handler ({ userId, teamId, accessLevel, limitClubAddOn }) {
				if (!teamId && !userId) {
					if (this._firestoreUnbinds['vuefireLimitNumRunnits']) {
						this.$unbind('vuefireLimitNumRunnits');
					}
					return;
				}

				const limit: number = limitClubAddOn?.unlimitedRunnits
					? 0
					: (limitClubAddOn?.numRunnitsLimit || FREE_PLAN_LIMIT_CLUB_ADD_ON_NUM_RUNNITS_LIMIT); // 0 for infinite

				this.setLoadingLimitNumRunnits(true);
				let newResult: Runnit[] = [];
				if (teamId && userId) {
					// index created: runnits - accessLevel Ascending deletedAt Ascending teamId Ascending type Ascending createdAt Descending __name__ Descending
					let runnitsRef: any = db.collection(`runnits`);
					if (limit > 0) {
						runnitsRef = runnitsRef
							.where('teamId', '==', teamId)
							.where('accessLevel', '==', accessLevel)
							.where('deletedAt', '==', null)
							.where('type', '==', RUNNIT_TYPE.WORKFLOW)
							.orderBy('createdAt', 'desc')
							.limit(limit);
					} else {
						// Guarantee no results
						runnitsRef = runnitsRef
							.where('nonexistentField', '==', '__never__')
							.limit(1);
					}
					if (accessLevel !== RUNNITS_ACCESS_LEVEL.SHARED) {
						// index created: runnits - accessLevel Ascending deletedAt Ascending teamId Ascending userId Ascending createdAt Descending __name__ Descending
						runnitsRef = runnitsRef.where('userId', '==', userId);
					}
					newResult = await this.$bind(
						'vuefireLimitNumRunnits',
						runnitsRef,
						get$bindFirestoreOptions(),
					);
				} else if (userId) {
					// index created: runnits - deletedAt Ascending teamId Ascending type Ascending userId Ascending createdAt Descending __name__ Descending
					let runnitsRef: any = db.collection(`runnits`);
					if (limit > 0) {
						runnitsRef = runnitsRef
							.where('userId', '==', userId)
							.where('teamId', '==', null)
							.where('deletedAt', '==', null)
							.where('type', '==', RUNNIT_TYPE.WORKFLOW)
							.orderBy('createdAt', 'desc')
							.limit(limit);
					} else {
						// Guarantee no results
						runnitsRef = runnitsRef
							.where('nonexistentField', '==', '__never__')
							.limit(1);
					}
					newResult = await this.$bind(
						'vuefireLimitNumRunnits',
						runnitsRef,
						get$bindFirestoreOptions(),
					);
				}

				this.setLoadingLimitNumRunnits(false);
				if (!newResult) this.resultLimitNumRunnits(newResult);
			},
		},
		boundPropsRunnitNodes: {
			immediate: true,
			async handler ({ runnitId }) {
				if (!runnitId) {
					if (this._firestoreUnbinds['vuefireRunnitNodes']) {
						this.$unbind('vuefireRunnitNodes');
					}
					return;
				}

				this.setLoadingRunnitNodes(true);
				let newResult: RunnitNode[] = [];
				// TODO: Add pagination
				// index created: runnitNodes deletedAt Ascending sortOrder Ascending __name__ Ascending
				newResult = await this.$bind(
					'vuefireRunnitNodes',
					db.collection(`runnits/${runnitId}/runnitNodes`)
						.where('deletedAt', '==', null)
						.orderBy('sortOrder', 'asc')
						.limit(100),
					get$bindFirestoreOptions(),
				);

				this.setLoadingRunnitNodes(false);
				if (!newResult) this.resultRunnitNodes(newResult);
			},
		},
		boundPropsTrainerNodeDefs: {
			immediate: true,
			async handler () {
				this.setLoadingTrainerNodeDefs(true);
				let query = db.collection('runnitNodeDefs')
					.where('appType', '==', RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER)
					.where('type', '==', RUNNIT_NODE_DEF_TOOL_TYPE.CORE)
					.where('isDeleted', '==', false);

				// Apply filter for non-admin users
				if (this.user && !this.user.isAdmin) {
					query = query.where('isPublished', '==', true);
				}

				try {
					const newResult: RunnitNodeDef[] = await this.$bind(
						'vuefireTrainerNodeDefs',
						query,
						get$bindFirestoreOptions(),
					);
					if (!newResult) await this.resultTrainerNodeDefs(newResult);
				} catch (error) {
					console.error('Failed to load TrainerNodeDefs:', error);
					this.resultTrainerNodeDefs([]); // Set to empty array in case of error
				} finally {
					this.setLoadingTrainerNodeDefs(false);
				}
			}
		},
		boundPropsPublicRunnitNodeDefs: {
			immediate: true,
			async handler () {
				this.setLoadingPublicRunnitNodeDefs(true);
				let query = db.collection('runnitNodeDefs')
					.where('appType', '==', RUNNIT_NODE_DEF_TOOL_APP_TYPE.RUNNIT)
					.where('type', '==', RUNNIT_NODE_DEF_TOOL_TYPE.CORE)
					.where('isDeleted', '==', false);

				// Apply filter for non-admin users
				if (this.user && !this.user.isAdmin) {
					query = query.where('isPublished', '==', true);
				}

				try {
					const newResult: RunnitNodeDef[] = await this.$bind(
						'vuefirePublicRunnitNodeDefs',
						query,
						get$bindFirestoreOptions(),
					);
					if (!newResult) this.resultPublicRunnitNodeDefs(newResult);
				} catch (error) {
					console.error('Failed to load PublicRunnitNodeDefs:', error);
					this.resultPublicRunnitNodeDefs([]); // Set to empty array in case of error
				} finally {
					this.setLoadingPublicRunnitNodeDefs(false);
				}
			},
		},
		boundPropsCompanyRunnitNodeDefs: {
			immediate: true,
			async handler ({ companyId }) {
				if (!companyId) {
					if (this._firestoreUnbinds['vuefireCompanyRunnitNodeDefs']) {
						this.$unbind('vuefireCompanyRunnitNodeDefs');
					}
					return;
				}

				this.setLoadingCompanyRunnitNodeDefs(true);
				let query = db.collection('runnitNodeDefs')
					.where('appType', '==', RUNNIT_NODE_DEF_TOOL_APP_TYPE.RUNNIT)
					.where('type', '==', RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY)
					.where('isDeleted', '==', false)
					.where(`companyIds.${companyId}`, '==', true);

				// Apply filter for non-admin users
				if (this.user && !this.user.isAdmin) {
					query = query.where('isPublished', '==', true);
				}

				try {
					const newResult: RunnitNodeDef[] = await this.$bind(
						'vuefireCompanyRunnitNodeDefs',
						query,
						get$bindFirestoreOptions(),
					);
					if (!newResult) this.resultCompanyRunnitNodeDefs(newResult);
				} catch (error) {
					console.error('Failed to load CompanyRunnitNodeDefs:', error);
					this.resultCompanyRunnitNodeDefs([]); // Set to empty array in case of error
				} finally {
					this.setLoadingCompanyRunnitNodeDefs(false);
				}
			},
		},
		boundPropsPublicRunnitNodeDefTags: {
			immediate: true,
			async handler () {
				this.setLoadingPublicRunnitNodeDefTags(true);
				const query = db.collection('runnitNodeDefTags')
					.where('isDeleted', '==', false);

				try {
					const newResult: RunnitNodeDef[] = await this.$bind(
						'vuefirePublicRunnitNodeDefTags',
						query,
						get$bindFirestoreOptions(),
					);
					if (!newResult) this.resultPublicRunnitNodeDefTags(newResult);
				} catch (error) {
					console.error('Failed to load PublicRunnitNodeDefTags:', error);
					this.resultPublicRunnitNodeDefTags([]); // Set to empty array in case of error
				} finally {
					this.setLoadingPublicRunnitNodeDefTags(false);
				}
			},
		},
		boundPropsRunnitKits: {
			immediate: true,
			async handler () {
				this.setLoadingRunnitKits(true);
				// Six documents, one per Kit Type, so no pagination and no ordering
				// clause: the Kit Resolver orders by sortOrder. Non-admins can only
				// read published, non-deleted Kits (firestore.rules), so the filters
				// here must match or the query is rejected outright.
				const collection = db.collection('runnitKits');
				const query = (this.user && this.user.isAdmin)
					? collection
					: collection
						.where('isPublished', '==', true)
						.where('isDeleted', '==', false);

				try {
					const newResult: RunnitKit[] = await this.$bind(
						'vuefireRunnitKits',
						query,
						get$bindFirestoreOptions(),
					);
					if (!newResult) this.resultRunnitKits(newResult);
				} catch (error) {
					console.error('Failed to load RunnitKits:', error);
					this.resultRunnitKits([]); // Set to empty array in case of error
				} finally {
					this.setLoadingRunnitKits(false);
				}
			},
		},
		boundPropsDraftRunnitNodeRun: {
			immediate: true,
			async handler ({ userId, nodeId }) {
				if (userId && nodeId) {
					this.setLoadingDraftRunnitNodeRun(true);
					// index created: runnitNodeRunDrafts: deletedAt Ascending nodeId Ascending userId Ascending draftAt Descending __name__ Descending
					const newResult: RunnitNodeRun[] = await this.$bind(
						'vuefireDraftRunnitNodeRuns',
						db.collection(`runnitNodeRunDrafts`)
							.where('userId', '==', userId)
							.where('nodeId', '==', nodeId)
							.where('deletedAt', '==', null)
							.orderBy('draftAt', 'desc')
							.limit(1),
						get$bindFirestoreOptions(),
					);
					this.setLoadingDraftRunnitNodeRun(false);
					if (!newResult) this.resultDraftRunnitNodeRun(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireDraftRunnitNodeRuns']) {
						this.$unbind('vuefireDraftRunnitNodeRuns');
					}
				}
			},
		},
		boundPropsAnnouncements: {
			immediate: true,
			async handler () {
				this.setLoadingAnnouncements(true);
				const newResult: Announcement[] = await this.$bind(
					'vuefireAnnouncements',
					db.collection('announcements')
						.where('show', '==', true)
						.limit(3),
					get$bindFirestoreOptions(),
				);
				this.setLoadingAnnouncements(false);
				if (!newResult) this.resultAnnouncements(newResult);
			},
		},
		boundPropsUser: {
			immediate: true,
			async handler ({ authUser }) {
				if (authUser) {
					this.setLoadingUser(true);
					const newResult: User = await this.$bind(
						'vuefireUser',
						db.doc(`users/${authUser.uid}`),
						get$bindFirestoreOptions(),
					);
					this.setLoadingUser(false);
					if (!newResult) this.resultUser(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireUser']) {
						this.$unbind('vuefireUser');
					}
				}
			},
		},
		boundPropsUserBalanceAccount: {
			immediate: true,
			async handler ({ user }) {
				if (user) {
					this.setLoadingUserBalanceAccount(true);
					const newResult: BalanceAccount = await this.$bind(
						'vuefireUserBalanceAccount',
						db.doc(`balanceAccounts/${user.id}`),
						get$bindFirestoreOptions(),
					);
					this.setLoadingUserBalanceAccount(false);
					if (!newResult) this.resultUserBalanceAccount(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireUserBalanceAccount']) {
						this.$unbind('vuefireUserBalanceAccount');
					}
				}
			},
		},
		boundPropsTeam: {
			immediate: true,
			async handler ({ teamId }) {
				if (teamId) {
					this.setLoadingTeam(true);
					setTeamId(teamId);
					const newResult: Team = await this.$bind(
						'vuefireTeam',
						db.doc(`teams/${teamId}`),
						get$bindFirestoreOptions(),
					);
					this.setLoadingTeam(false);
					if (!newResult) this.resultTeam(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireTeam']) {
						removeTeamId();
						this.$unbind('vuefireTeam');
					}
				}
			},
		},
		boundPropsTeamBalanceAccount: {
			immediate: true,
			async handler ({ teamId }) {
				if (teamId) {
					this.setLoadingTeamBalanceAccount(true);
					const newResult: BalanceAccount = await this.$bind(
						'vuefireTeamBalanceAccount',
						db.doc(`balanceAccounts/${teamId}`),
						get$bindFirestoreOptions(),
					);
					this.setLoadingTeamBalanceAccount(false);
					if (!newResult) this.resultTeamBalanceAccount(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireTeamBalanceAccount']) {
						this.$unbind('vuefireTeamBalanceAccount');
					}
				}
			},
		},
		boundPropsStripeCustomer: {
			immediate: true,
			async handler ({ user }) {
				if (user) {
					this.setLoadingStripeCustomer(true);
					const newResult: StripeCustomer = await this.$bind(
						'vuefireStripeCustomer',
						db.doc(`stripeCustomers/${user.id}`),
						get$bindFirestoreOptions(),
					);
					this.setLoadingStripeCustomer(false);
					if (!newResult) this.resultStripeCustomer(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireStripeCustomer']) {
						this.$unbind('vuefireStripeCustomer');
					}
				}
			},
		},
		boundPropsStripeTeamsCustomer: {
			immediate: true,
			async handler ({ teamId }) {
				if (teamId) {
					this.setLoadingStripeTeamsCustomer(true);
					const newResult: StripeCustomer = await this.$bind(
						'vuefireStripeTeamsCustomer',
						db.doc(`stripeTeamsCustomers/${teamId}`),
						get$bindFirestoreOptions(),
					);
					this.setLoadingStripeTeamsCustomer(false);
					if (!newResult) this.resultStripeTeamsCustomer(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireStripeTeamsCustomer']) {
						this.$unbind('vuefireStripeTeamsCustomer');
					}
				}
			},
		},
		boundPropsCurrSessions: {
			immediate: true,
			async handler ({ user }) {
				if (user) {
					this.setLoadingCurrSessions(true);
					const newResult: Session[] = await this.$bind(
						'vuefireCurrSessions',
						// index created: sessions: state Ascending userId Ascending createdAt Descending __name__ Descending
						db.collection('sessions')
							.where('state', 'in', CURR_SESSION_STATES)
							.where('userId', '==', user.id)
							.orderBy('createdAt', 'desc'),
						get$bindFirestoreOptions(),
					);
					this.setLoadingCurrSessions(false);
					if (!newResult) this.resultCurrSessions(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireCurrSessions']) {
						this.$unbind('vuefireCurrSessions');
					}
				}
			},
		},
		boundPropsCurrSessionBackgroundTasks: {
			immediate: true,
			async handler ({ session }) {
				if (session) {
					this.setLoadingCurrSessionBackgroundTasks(true);
					const newResult: SessionBackgroundTask[] = await this.$bind(
						'vuefireCurrSessionBackgroundTasks',
						db.collection('sessions')
							.doc(session.id)
							.collection('backgroundTasks')
							.orderBy('createdAt', 'desc')
							.limit(100),
						get$bindFirestoreOptions(),
					);
					this.setLoadingCurrSessionBackgroundTasks(false);
					if (!newResult) this.resultCurrSessionBackgroundTasks(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireCurrSessionBackgroundTasks']) {
						this.$unbind('vuefireCurrSessionBackgroundTasks');
					}
				}
			},
		},
		boundPropsAdminRegularSessions: {
			immediate: true,
			async handler ({ user }) {
				if (user && user.isAdmin) {
					this.setLoadingAdminRegularSessions(true);
					const newResult: Session[] = await this.$bind(
						'vuefireAdminRegularSessions',
						db.collection('sessions')
							.where('state', 'in', CURR_SESSION_STATES),
						get$bindFirestoreOptions(),
					);
					this.setLoadingAdminRegularSessions(false);
					if (!newResult) this.resultAdminRegularSessions(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireAdminRegularSessions']) {
						this.$unbind('vuefireAdminRegularSessions');
					}
				}
			},
		},
		boundPropsStableDiffusionModels: {
			immediate: true,
			async handler () {
				this.setLoadingStableDiffusionModels(true);
				const newResult: StableDiffusionModel[] = await this.$bind(
					'vuefireStableDiffusionModels',
					db.collection('stableDiffusionModels')
						.orderBy('name', 'asc'),
					get$bindFirestoreOptions(),
				);
				this.setLoadingStableDiffusionModels(false);
				if (!newResult) this.resultStableDiffusionModels(newResult);
			},
		},
		boundPropsSetupSessionPrefill: {
			immediate: true,
			async handler ({ authUser, appKey }) {
				if (authUser && appKey) {
					this.setLoadingSetupSessionPrefill(true);
					const newResult: SetupSessionPrefill = await this.$bind(
						'vuefireSetupSessionPrefill',
						db.doc(`users/${authUser.uid}/setupSessionPrefills/${appKey}`),
						get$bindFirestoreOptions(),
					);
					this.setLoadingSetupSessionPrefill(false);
					if (!newResult) this.resultSetupSessionPrefill(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireSetupSessionPrefill']) {
						this.$unbind('vuefireSetupSessionPrefill');
					}
				}
			},
		},
		boundPropsRecentUserRunnitNodeDefInfos: {
			immediate: true,
			async handler ({ user }) {
				if (user) {
					this.setLoadingRecentUserRunnitNodeDefInfos(true);
					// index created: runnitNodeDefInfos: 	appType Ascending lastOpenAt Descending __name__ Descending
					const newResult: UserRunnitNodeDefInfo[] = await this.$bind(
						'vuefireRecentUserRunnitNodeDefInfos',
						db.collection(`users/${user.id}/runnitNodeDefInfos`)
							.orderBy('lastOpenAt', 'desc')
							.limit(15),
						get$bindFirestoreOptions(),
					);
					this.setLoadingRecentUserRunnitNodeDefInfos(false);
					if (!newResult) this.resultRecentUserRunnitNodeDefInfos(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireRecentUserRunnitNodeDefInfos']) {
						this.$unbind('vuefireRecentUserRunnitNodeDefInfos');
					}
				}
			},
		},
		boundPropsRecentUserWorkflowRunnitInfos: {
			immediate: true,
			async handler ({ user, team }) {
				if (user) {
					this.setLoadingRecentUserWorkflowRunnitInfos(true);
					// index created: workflowRunnitInfos: 	teamId Ascending lastOpenAt Descending __name__ Descending
					const newResult: UserWorkflowRunnitInfo[] = await this.$bind(
						'vuefireRecentUserWorkflowRunnitInfos',
						db.collection(`users/${user.id}/workflowRunnitInfos`)
							.where('teamId', '==', team?.id || null)
							.orderBy('lastOpenAt', 'desc')
							.limit(15),
						get$bindFirestoreOptions(),
					);
					this.setLoadingRecentUserWorkflowRunnitInfos(false);
					if (!newResult) this.resultRecentUserWorkflowRunnitInfos(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireRecentUserWorkflowRunnitInfos']) {
						this.$unbind('vuefireRecentUserWorkflowRunnitInfos');
					}
				}
			},
		},
		boundPropsModels: {
			immediate: true,
			async handler ({ userId, teamId, accessLevel }) {
				if (userId || teamId) {
					this.setLoadingModels(true);
					let newResult: Model[] = [];

					if (teamId && userId) {
						// index created: models - teamId Ascending accessLevel Ascending isDeleted Ascending createdAt Descending __name__ Descending
						let modelsRef: any = db.collection(`models`)
							.where('teamId', '==', teamId)
							.where('accessLevel', '==', accessLevel)
							.where('isDeleted', '==', false)
							.orderBy('createdAt', 'desc')
							.limit(1000);
						if (accessLevel !== RUNNITS_ACCESS_LEVEL.SHARED) {
							// index created: models - accessLevel Ascending deletedAt Ascending teamId Ascending userId Ascending createdAt Descending __name__ Descending
							modelsRef = modelsRef.where('userId', '==', userId);
						}
						newResult = await this.$bind(
							'vuefireModels',
							modelsRef,
							get$bindFirestoreOptions(),
						);
					} else if (userId) {
						// index created: models - isDeleted Ascending teamId Ascending userId Ascending createdAt Descending __name__ Descending
						const modelsRef: any = db.collection(`models`)
							.where('userId', '==', userId)
							.where('teamId', '==', null)
							.where('isDeleted', '==', false)
							.orderBy('createdAt', 'desc')
							.limit(100);
						newResult = await this.$bind(
							'vuefireModels',
							modelsRef,
							get$bindFirestoreOptions(),
						);
					}

					this.setLoadingModels(false);
					if (!newResult) this.resultModels(newResult);
				} else {
					if (this._firestoreUnbinds['vuefireModels']) {
						this.$unbind('vuefireModels');
					}
				}
			},
		},
		boundPropsCompanyUserRole: {
			immediate: true,
			async handler (boundProps) {
				const { userId, companyId } = boundProps || {};
				if (userId && companyId) {
					this.setLoadingCompanyUserRole(true);
					try {
						const companyUserRolesRef = db.collection('companyUserRoles').doc(`${companyId}_${userId}`);

						// Bind to single document
						const newResult: CompanyUserRole = await this.$bind(
							'vuefireCompanyUserRole',
							companyUserRolesRef,
							get$bindFirestoreOptions(),
						);

						this.setLoadingCompanyUserRole(false);
						this.resultCompanyUserRole(newResult);
					} catch (error) {
						console.error('Error binding company user roles:', error);
						this.setLoadingCompanyUserRole(false);
					}
				} else {
					if (this._firestoreUnbinds['vuefireCompanyUserRole']) {
						this.$unbind('vuefireCompanyUserRole');
					}
				}
			},
		},

		boundPropsTeamUserRole: {
			immediate: true,
			async handler (boundProps) {
				const { userId, teamId } = boundProps || {};
				if (userId && teamId) {
					this.setLoadingTeamUserRole(true);
					try {
						const teamUserRoleRef = db.collection('teamUserRoles').doc(`${teamId}_${userId}`);
						const newResult = await this.$bind(
							'vuefireTeamUserRole',
							teamUserRoleRef,
							get$bindFirestoreOptions(),
						);
						this.setLoadingTeamUserRole(false);
						this.resultTeamUserRole(newResult);
					} catch (error) {
						console.error('Error binding team user roles:', error);
						this.setLoadingTeamUserRole(false);
					}
				} else {
					if (this._firestoreUnbinds['vuefireTeamUserRole']) {
						this.$unbind('vuefireTeamUserRole');
					}
				}
			},
		},

		/*
		Actual bindings
		 */
		vuefireClubOffers: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: ClubOffer[]) {
				this.resultClubOffers(newVal);
			},
		},
		vuefireAppOffers: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: AppOffer[]) {
				if (newVal && newVal.length) {
					const newValArray: AppOffer[] = Array.from(newVal || [])
						.filter(({ publishedAt, isDev }) => !!(
							publishedAt ||
							this.user?.isAdmin ||
							(this.user?.isDev && isDev)
						))
						.filter((appOffer: AppOffer) => (
							this.user?.isAdmin ||
							(
								(
									(!appOffer.clubs || !appOffer.clubs.length) &&
									!appOffer.isTeam
								) ||
								(
									appOffer.isTeam &&
									this.team &&
									this.team.isActive
								) ||
								(
									appOffer.clubs &&
									appOffer.clubs.length &&
									this.user &&
									this.user.club &&
									appOffer.clubs.includes(this.user.club)
								)
							)
						));
					this.resultAppOffers(newValArray);
					return;
				}

				this.resultAppOffers(newVal);
			},
		},
		vuefireHardwareOffers: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: HardwareOffer[]) {
				if (newVal && newVal.length) {
					const newValArray: HardwareOffer[] = Array.from(newVal || [])
						.filter(({ publishedAt, isDev }) => !!(
							publishedAt ||
							this.user?.isAdmin ||
							(this.user?.isDev && isDev)
						))
						.filter((hardwareOffer: HardwareOffer) => (
							this.user?.isAdmin ||
							(
								(
									(!hardwareOffer.clubs || !hardwareOffer.clubs.length) &&
									!hardwareOffer.isTeam
								) ||
								(
									hardwareOffer.isTeam &&
									this.team &&
									this.team.isActive
								) ||
								(
									hardwareOffer.clubs &&
									hardwareOffer.clubs.length &&
									this.user &&
									this.user.club &&
									hardwareOffer.clubs.includes(this.user.club)
								)
							)
						));
					this.resultHardwareOffers(newValArray);
					return;
				}

				this.resultHardwareOffers(newVal);
			},
		},
		vuefireSoftwareOffers: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: SoftwareOffer[]) {
				if (newVal && newVal.length) {
					const newValArray: SoftwareOffer[] = Array.from(newVal || [])
						.filter(({ publishedAt, isDev }) => !!(
							publishedAt ||
							this.user?.isAdmin ||
							(this.user?.isDev && isDev)
						))
						.filter((softwareOffer: SoftwareOffer) => (
							this.user?.isAdmin ||
							(
								(
									(!softwareOffer.clubs || !softwareOffer.clubs.length) &&
									!softwareOffer.isTeam
								) ||
								(
									softwareOffer.isTeam &&
									this.team &&
									this.team.isActive
								) ||
								(
									softwareOffer.clubs &&
									softwareOffer.clubs.length &&
									this.user &&
									this.user.club &&
									softwareOffer.clubs.includes(this.user.club)
								)
							)
						));
					this.resultSoftwareOffers(newValArray);
					return;
				}

				this.resultSoftwareOffers(newVal);
			},
		},
		vuefireDeploymentImagesSystem: {
			immediate: false,
			deep: false,
			handler () { this._mergeAndCommitDeploymentImages(); },
		},
		vuefireDeploymentImagesOwn: {
			immediate: false,
			deep: false,
			handler () { this._mergeAndCommitDeploymentImages(); },
		},
		vuefireDeploymentImagesTeam: {
			immediate: false,
			deep: false,
			handler () { this._mergeAndCommitDeploymentImages(); },
		},
		vuefireStableDiffusionOffers: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: StableDiffusionOffer[]) {
				this.resultStableDiffusionOffers(newVal);
			},
		},
		vuefireWorkspaces: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: Workspace[]) {
				this.resultWorkspaces(newVal);
			},
		},
		vuefireLimitNumRunnits: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: Runnit[]) {
				this.resultLimitNumRunnits(newVal);
			},
		},
		vuefireRunnitNodes: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: RunnitNode[]) {
				this.resultRunnitNodes(newVal);
			},
		},
		vuefireTrainerNodeDefs: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: RunnitNodeDef[]) {
				this.resultTrainerNodeDefs(newVal);
			},
		},
		vuefirePublicRunnitNodeDefs: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: RunnitNodeDef[]) {
				this.resultPublicRunnitNodeDefs(newVal);
			},
		},
		vuefireCompanyRunnitNodeDefs: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: RunnitNodeDef[]) {
				this.resultCompanyRunnitNodeDefs(newVal);
			},
		},
		vuefirePublicRunnitNodeDefTags: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: RunnitNodeDefTag[]) {
				if (newVal && newVal.length) {
					const newValArray: RunnitNodeDefTag[] = Array.from(newVal || [])
						.filter(({ type, label }) => !!(
							this.user?.isAdmin ||
							(type !== RUNNIT_NODE_DEF_TAG_TYPE.INTERNAL && type !== RUNNIT_NODE_DEF_TAG_TYPE.HOME_PAGE) ||
							(type === RUNNIT_NODE_DEF_TAG_TYPE.INTERNAL && label === 'App') ||
							(type === RUNNIT_NODE_DEF_TAG_TYPE.INTERNAL && label === 'Tool') ||
							(type === RUNNIT_NODE_DEF_TAG_TYPE.HOME_PAGE && label === 'Featured')
						));
					this.resultPublicRunnitNodeDefTags(newValArray);
					return;
				}

				this.resultPublicRunnitNodeDefTags(newVal);
			},
		},
		vuefireRunnitKits: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: RunnitKit[]) {
				this.resultRunnitKits(newVal);
			},
		},
		vuefireDraftRunnitNodeRuns: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: RunnitNodeRun[]) {
				this.resultDraftRunnitNodeRun(newVal);
			},
		},
		vuefireAnnouncements: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: Announcement[]) {
				this.resultAnnouncements(newVal);
			},
		},
		vuefireUser: {
			immediate: false,
			deep: true, // true only on objects, not arrays
			handler (newVal: User) {
				this.resultUser(newVal);
			},
		},
		vuefireUserBalanceAccount: {
			immediate: false,
			deep: true, // true only on objects, not arrays
			handler (newVal: BalanceAccount) {
				this.resultUserBalanceAccount(newVal);
			},
		},
		vuefireTeam: {
			immediate: false,
			deep: true, // true only on objects, not arrays
			handler (newVal: Team) {
				this.resultTeam(newVal);
			},
		},
		vuefireTeamBalanceAccount: {
			immediate: false,
			deep: true, // true only on objects, not arrays
			handler (newVal: BalanceAccount) {
				this.resultTeamBalanceAccount(newVal);
			},
		},
		vuefireStripeCustomer: {
			immediate: false,
			deep: true, // true only on objects, not arrays
			handler (newVal: StripeCustomer) {
				this.resultStripeCustomer(newVal);
			},
		},
		vuefireStripeTeamsCustomer: {
			immediate: false,
			deep: true, // true only on objects, not arrays
			handler (newVal: StripeCustomer) {
				this.resultStripeTeamsCustomer(newVal);
			},
		},
		vuefireCurrSessions: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: Session[]) {
				this.resultCurrSessions(newVal);
			},
		},
		vuefireCurrSessionBackgroundTasks: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: SessionBackgroundTask[]) {
				this.resultCurrSessionBackgroundTasks(newVal);
			},
		},
		vuefireAdminRegularSessions: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: Session[]) {
				this.resultAdminRegularSessions(newVal);
			},
		},
		vuefireStableDiffusionModels: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: StableDiffusionModel[]) {
				this.resultStableDiffusionModels(newVal);
			},
		},
		vuefireSetupSessionPrefill: {
			immediate: false,
			deep: true, // true only on objects, not arrays
			handler (newVal: SetupSessionPrefill) {
				this.resultSetupSessionPrefill(newVal);
			},
		},
		vuefireRecentUserRunnitNodeDefInfos: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: UserRunnitNodeDefInfo[]) {
				this.resultRecentUserRunnitNodeDefInfos(newVal);
			},
		},
		vuefireRecentUserWorkflowRunnitInfos: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: UserWorkflowRunnitInfo[]) {
				this.resultRecentUserWorkflowRunnitInfos(newVal);
			},
		},
		vuefireModels: {
			immediate: false,
			deep: false, // true only on objects, not arrays
			handler (newVal: Model[]) {
				this.resultModels(newVal);
			},
		},
		vuefireCompanyUserRole: {
			immediate: false,
			deep: true, // true for objects since we're binding to a single document
			handler (newVal: CompanyUserRole) {
				this.resultCompanyUserRole(newVal);
			},
		},

		vuefireTeamUserRole: {
			immediate: false,
			deep: true,
			handler (newVal) {
				this.resultTeamUserRole(newVal);
			},
		},

	},
	methods: {
		...mapActions([
			// result
			'resultClubOffers',
			'resultAppOffers',
			'resultHardwareOffers',
			'resultSoftwareOffers',
			'resultDeploymentImages',
			'resultStableDiffusionOffers',
			'resultWorkspaces',
			'resultLimitNumRunnits',
			'resultRunnitNodes',
			'resultTrainerNodeDefs',
			'resultPublicRunnitNodeDefs',
			'resultCompanyRunnitNodeDefs',
			'resultPublicRunnitNodeDefTags',
			'resultRunnitKits',
			'resultDraftRunnitNodeRun',
			'resultAnnouncements',
			'resultUser',
			'resultUserBalanceAccount',
			'resultTeam',
			'resultTeamBalanceAccount',
			'resultStripeCustomer',
			'resultStripeTeamsCustomer',
			'resultCurrSessions',
			'resultCurrSessionBackgroundTasks',
			'resultAdminRegularSessions',
			'resultStableDiffusionModels',
			'resultSetupSessionPrefill',
			'resultRecentUserRunnitNodeDefInfos',
			'resultRecentUserWorkflowRunnitInfos',
			'resultModels',
			'resultCompanyUserRole',
			'resultTeamUserRole',

			// setLoading
			'setLoadingClubOffers',
			'setLoadingAppOffers',
			'setLoadingHardwareOffers',
			'setLoadingSoftwareOffers',
			'setLoadingDeploymentImages',
			'setLoadingStableDiffusionOffers',
			'setLoadingWorkspaces',
			'setLoadingLimitNumRunnits',
			'setLoadingRunnitNodes',
			'setLoadingTrainerNodeDefs',
			'setLoadingPublicRunnitNodeDefs',
			'setLoadingCompanyRunnitNodeDefs',
			'setLoadingPublicRunnitNodeDefTags',
			'setLoadingRunnitKits',
			'setLoadingDraftRunnitNodeRun',
			'setLoadingAnnouncements',
			'setLoadingUser',
			'setLoadingUserBalanceAccount',
			'setLoadingTeam',
			'setLoadingTeamBalanceAccount',
			'setLoadingStripeCustomer',
			'setLoadingStripeTeamsCustomer',
			'setLoadingCurrSessions',
			'setLoadingCurrSessionBackgroundTasks',
			'setLoadingAdminRegularSessions',
			'setLoadingStableDiffusionModels',
			'setLoadingSetupSessionPrefill',
			'setLoadingRecentUserRunnitNodeDefInfos',
			'setLoadingRecentUserWorkflowRunnitInfos',
			'setLoadingModels',
			'setLoadingCompanyUserRole',
			'setLoadingTeamUserRole',
		]),
		_mergeAndCommitDeploymentImages () {
			const byId: Record<string, DeploymentImage> = {};
			const collect = (arr: DeploymentImage[]) => {
				if (!_isArray(arr)) return;
				for (const d of arr) {
					if (d && (d as any).id) byId[(d as any).id] = d;
				}
			};
			collect(this.vuefireDeploymentImagesSystem);
			collect(this.vuefireDeploymentImagesOwn);
			collect(this.vuefireDeploymentImagesTeam);

			// Entitlement filter: clubs / isTeam access (ownership is now enforced at the query layer).
			const filtered: DeploymentImage[] = Object.values(byId).filter((deploymentImage: DeploymentImage) => (
				this.user?.isAdmin ||
				(
					(
						(!deploymentImage.clubs || !deploymentImage.clubs.length) &&
						!deploymentImage.isTeam
					) ||
					(
						deploymentImage.isTeam &&
						this.team &&
						this.team.isActive
					) ||
					(
						deploymentImage.clubs &&
						deploymentImage.clubs.length &&
						this.user &&
						this.user.club &&
						deploymentImage.clubs.includes(this.user.club)
					)
				)
			));
			this.resultDeploymentImages(filtered);
		},
	},
});
